/**
 * The first thing you see: your routes.
 *
 * The planner used to be the home page, so opening the site put you in front
 * of an empty map with a dialog behind a button to reach anything you had
 * already made. Choosing a route is the more common errand by a long way, and
 * making a new one is one button from here.
 */

import { useCallback, useEffect, useState } from 'react';
import { formatDistance, formatDuration } from '@cad/core';
import { deleteRoute, listRoutes, type RouteSummary } from '../api';

function whenText(timestamp: number): string {
  const days = Math.floor((Date.now() - timestamp) / 86_400_000);
  if (days < 1) return 'today';
  if (days === 1) return 'yesterday';
  if (days < 30) return `${days} days ago`;
  return new Date(timestamp).toLocaleDateString('en-GB', {
    day: 'numeric',
    month: 'short',
    year: 'numeric',
  });
}

export function RoutesHome({ username, onSignOut }: { username: string; onSignOut: () => void }) {
  const [routes, setRoutes] = useState<RouteSummary[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [copiedId, setCopiedId] = useState<string | null>(null);
  const [confirmingId, setConfirmingId] = useState<string | null>(null);

  const load = useCallback(() => {
    listRoutes()
      .then(setRoutes)
      .catch((problem: unknown) =>
        setError(problem instanceof Error ? problem.message : 'Could not load your routes'),
      );
  }, []);

  useEffect(load, [load]);

  async function copyLink(id: string) {
    const url = `${window.location.origin}/r/${id}`;
    try {
      await navigator.clipboard.writeText(url);
      setCopiedId(id);
      setTimeout(() => setCopiedId((current) => (current === id ? null : current)), 2000);
    } catch {
      window.prompt('Copy this link', url);
    }
  }

  async function remove(id: string) {
    try {
      await deleteRoute(id);
      setRoutes((current) => (current ?? []).filter((route) => route.id !== id));
    } catch (problem: unknown) {
      setError(problem instanceof Error ? problem.message : 'Could not delete that route');
    } finally {
      setConfirmingId(null);
    }
  }

  return (
    <div className="home">
      <header className="home-header">
        <div>
          <h1>CAD</h1>
          <p className="home-subtitle">Routes for the group</p>
        </div>
        <div className="home-account">
          <span className="account-who">{username}</span>
          <button type="button" className="account-out" onClick={onSignOut}>
            Sign out
          </button>
        </div>
      </header>

      <div className="home-actions">
        <a className="primary-button" href="/?new">
          New route
        </a>
      </div>

      {error && <p className="signin-error">{error}</p>}
      {!routes && !error && <p className="fine-print">Loading…</p>}
      {routes?.length === 0 && (
        <p className="home-empty">
          Nothing saved yet. <a href="/?new">Plot your first route</a> — click the map to drop
          points, drag the line onto the roads you want, then Save.
        </p>
      )}

      {routes && routes.length > 0 && (
        <ul className="home-list">
          {routes.map((route) => (
            <li key={route.id} className="home-row">
              <a className="home-open" href={`/?r=${route.id}`}>
                <span className="home-name">{route.name}</span>
                {(route.startName || route.endName) && (
                  <span className="home-ends">
                    {route.startName && (
                      <span className="home-end">
                        <i className="dot-start" /> {route.startName}
                      </span>
                    )}
                    {route.endName && (
                      <span className="home-end">
                        <i className="dot-end" /> {route.endName}
                      </span>
                    )}
                  </span>
                )}
                <span className="home-meta">
                  {formatDistance(route.distance)} · {formatDuration(route.duration)} ·{' '}
                  {route.stops} stops · edited {whenText(route.updatedAt)}
                </span>
              </a>

              <div className="home-row-actions">
                <button type="button" onClick={() => copyLink(route.id)}>
                  {copiedId === route.id ? 'Copied' : 'Link'}
                </button>
                <a className="link-button" href={`/?stats=${route.id}`} target="_blank" rel="noopener">
                  Stats
                </a>
                <a className="link-button" href={`/api/routes/${route.id}/gpx`}>
                  GPX
                </a>
                {confirmingId === route.id ? (
                  <button type="button" className="danger" onClick={() => remove(route.id)}>
                    Sure?
                  </button>
                ) : (
                  <button type="button" onClick={() => setConfirmingId(route.id)}>
                    Remove
                  </button>
                )}
              </div>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
